import { FC, useEffect, useState } from 'react'
import { Box, Typography } from '@mui/material'
import { EstadoDeLista } from '@/components/common/EstadoDeLista'
import { TituloSecao } from '@/components/dashboard/TituloSecao'
import { documentoService } from '@/services/documentoService'
import { CampanhaResumoDashboard } from '@/types/dashboard'
import { tokens } from '@/theme'
import { formatDate } from '@/utils/formatters'

export interface ArteCampanhaProps {
  campanha: CampanhaResumoDashboard | null | undefined
  carregando?: boolean
}

/**
 * Arte, nome, vigência e premiação da campanha vigente (VC-02b, AN-5 §4).
 *
 * A imagem vem do stream `GET /v1/documentos/{id}/arquivo` (o único download que o
 * browser alcança) e vira um object URL, revogado ao trocar/desmontar. Qualquer falha —
 * 403 (sem política para CAMPANHA), 404, documento em quarentena, rede — só tira a
 * imagem: nome, vigência e premiação continuam.
 */
export const ArteCampanha: FC<ArteCampanhaProps> = ({ campanha, carregando = false }) => {
  const arteId = campanha?.arteDocumentoId ?? null
  const [url, setUrl] = useState<string | null>(null)

  useEffect(() => {
    setUrl(null)
    if (!arteId || typeof URL.createObjectURL !== 'function') return
    let ativo = true
    let criada: string | null = null
    documentoService
      .baixarArquivo(arteId)
      .then((blob) => {
        if (!ativo) return
        criada = URL.createObjectURL(blob)
        setUrl(criada)
      })
      .catch(() => {
        if (ativo) setUrl(null)
      })
    return () => {
      ativo = false
      if (criada) URL.revokeObjectURL(criada)
    }
  }, [arteId])

  return (
    <>
      <TituloSecao titulo="Campanha" descricao="Campanha vigente e premiação." />
      {carregando ? (
        <EstadoDeLista estado="carregando" minHeight={160} />
      ) : !campanha ? (
        <EstadoDeLista estado="vazio" mensagem="Nenhuma campanha vigente" minHeight={160} />
      ) : (
        <Box>
          {url && (
            <Box
              component="img"
              src={url}
              alt={`Arte da campanha ${campanha.nome}`}
              sx={{
                width: '100%',
                maxHeight: 180,
                objectFit: 'cover',
                borderRadius: 1,
                mb: 1.5,
                display: 'block',
              }}
            />
          )}
          <Typography sx={{ fontWeight: 700, color: tokens.brandInk }}>{campanha.nome}</Typography>
          <Typography variant="caption" component="div" sx={{ color: tokens.textSecondary }}>
            {`Vigência: ${formatDate(campanha.dataInicio)} a ${formatDate(campanha.dataFim)}`}
          </Typography>
          {campanha.premiacao && (
            <Typography variant="body2" sx={{ mt: 1, color: tokens.brandInk }}>
              Premiação: {campanha.premiacao}
            </Typography>
          )}
        </Box>
      )}
    </>
  )
}

export default ArteCampanha
